/**
 * MapEventService - Event handling and debouncing for map operations
 *
 * This service provides clean event handling between Leaflet and Vue,
 * preventing unnecessary re-renders and performance issues.
 */

class MapEventService {
  constructor() {
    this.debounceTimers = new Map();
    this.eventHandlers = new Map();
  }

  /**
   * Creates a debounced function that delays execution
   * @param {Function} func - Function to debounce
   * @param {number} delay - Delay in milliseconds
   * @param {string} key - Unique key for this debounced function
   * @returns {Function} Debounced function
   */
  debounce(func, delay = 300, key = "default") {
    return (...args) => {
      // Clear existing timer for this key
      if (this.debounceTimers.has(key)) {
        clearTimeout(this.debounceTimers.get(key));
      }

      // Set new timer
      const timer = setTimeout(() => {
        func.apply(this, args);
        this.debounceTimers.delete(key);
      }, delay);

      this.debounceTimers.set(key, timer);
    };
  }

  /**
   * Creates a throttled function that limits execution frequency
   * @param {Function} func - Function to throttle
   * @param {number} limit - Time limit in milliseconds
   * @param {string} key - Unique key for this throttled function
   * @returns {Function} Throttled function
   */
  throttle(func, limit = 100, key = "default") {
    let inThrottle = false;
    const throttleKey = `throttle_${key}`;

    return (...args) => {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;

        setTimeout(() => {
          inThrottle = false;
        }, limit);
      }
    };
  }

  /**
   * Registers an event handler with automatic cleanup
   * @param {L.Evented} target - Event target (map, layer, etc.)
   * @param {string} eventType - Event type
   * @param {Function} handler - Event handler function
   * @param {string} key - Unique key for this handler
   */
  registerHandler(target, eventType, handler, key) {
    // Store handler reference for cleanup
    const handlerKey = `${key}_${eventType}`;

    // Remove existing handler if present
    this.removeHandler(handlerKey);

    // Add new handler
    target.on(eventType, handler);
    this.eventHandlers.set(handlerKey, { target, eventType, handler });
  }

  /**
   * Removes a registered event handler
   * @param {string} key - Handler key
   */
  removeHandler(key) {
    if (this.eventHandlers.has(key)) {
      const { target, eventType, handler } = this.eventHandlers.get(key);
      target.off(eventType, handler);
      this.eventHandlers.delete(key);
    }
  }

  /**
   * Removes all registered handlers
   */
  cleanup() {
    // Clear all event handlers
    for (const [key, { target, eventType, handler }] of this.eventHandlers) {
      target.off(eventType, handler);
    }
    this.eventHandlers.clear();

    // Clear all debounce timers
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer);
    }
    this.debounceTimers.clear();
  }

  /**
   * Creates a map change handler with proper debouncing
   * @param {Function} onChange - Change callback function
   * @param {number} debounceDelay - Debounce delay in milliseconds
   * @returns {Function} Debounced change handler
   */
  createChangeHandler(onChange, debounceDelay = 300) {
    return this.debounce(
      (changeData) => {
        // Ensure we don't pass reactive data to the callback
        const cleanChangeData = JSON.parse(JSON.stringify(changeData));
        onChange(cleanChangeData);
      },
      debounceDelay,
      "geometryChange",
    );
  }

  /**
   * Creates a vertex drag handler with throttling for smooth updates
   * @param {Function} onVertexDrag - Vertex drag callback
   * @param {number} throttleLimit - Throttle limit in milliseconds
   * @returns {Function} Throttled vertex drag handler
   */
  createVertexDragHandler(onVertexDrag, throttleLimit = 50) {
    return this.throttle(
      (dragData) => {
        onVertexDrag(dragData);
      },
      throttleLimit,
      "vertexDrag",
    );
  }

  /**
   * Creates a map viewport change handler with debouncing
   * @param {Function} onViewportChange - Viewport change callback
   * @param {number} debounceDelay - Debounce delay in milliseconds
   * @returns {Function} Debounced viewport change handler
   */
  createViewportChangeHandler(onViewportChange, debounceDelay = 200) {
    return this.debounce(
      (viewportData) => {
        onViewportChange(viewportData);
      },
      debounceDelay,
      "viewportChange",
    );
  }

  /**
   * Sets up standard map event handlers
   * @param {L.Map} map - Leaflet map instance
   * @param {Object} handlers - Object containing handler functions
   */
  setupMapEvents(map, handlers = {}) {
    const {
      onZoomEnd,
      onMoveEnd,
      onResize,
      onClick,
      onLayerAdd,
      onLayerRemove,
    } = handlers;

    if (onZoomEnd) {
      this.registerHandler(
        map,
        "zoomend",
        this.createViewportChangeHandler(onZoomEnd),
        "map_zoom",
      );
    }

    if (onMoveEnd) {
      this.registerHandler(
        map,
        "moveend",
        this.createViewportChangeHandler(onMoveEnd),
        "map_move",
      );
    }

    if (onResize) {
      this.registerHandler(
        map,
        "resize",
        this.debounce(onResize, 100, "mapResize"),
        "map_resize",
      );
    }

    if (onClick) {
      this.registerHandler(map, "click", onClick, "map_click");
    }

    if (onLayerAdd) {
      this.registerHandler(map, "layeradd", onLayerAdd, "map_layeradd");
    }

    if (onLayerRemove) {
      this.registerHandler(
        map,
        "layerremove",
        onLayerRemove,
        "map_layerremove",
      );
    }
  }

  /**
   * Sets up layer editing event handlers
   * @param {L.Layer} layer - Leaflet layer
   * @param {Object} handlers - Object containing handler functions
   * @param {string} layerId - Unique layer identifier
   */
  setupLayerEvents(layer, handlers = {}, layerId) {
    const {
      onEdit,
      onDelete,
      onDragStart,
      onDrag,
      onDragEnd,
      onClick,
      onMouseOver,
      onMouseOut,
    } = handlers;

    if (onEdit) {
      this.registerHandler(
        layer,
        "edit",
        this.createChangeHandler(onEdit),
        `layer_${layerId}_edit`,
      );
    }

    if (onDelete) {
      this.registerHandler(
        layer,
        "delete",
        onDelete,
        `layer_${layerId}_delete`,
      );
    }

    if (onDragStart) {
      this.registerHandler(
        layer,
        "dragstart",
        onDragStart,
        `layer_${layerId}_dragstart`,
      );
    }

    if (onDrag) {
      this.registerHandler(
        layer,
        "drag",
        this.createVertexDragHandler(onDrag),
        `layer_${layerId}_drag`,
      );
    }

    if (onDragEnd) {
      this.registerHandler(
        layer,
        "dragend",
        this.createChangeHandler(onDragEnd),
        `layer_${layerId}_dragend`,
      );
    }

    if (onClick) {
      this.registerHandler(layer, "click", onClick, `layer_${layerId}_click`);
    }

    if (onMouseOver) {
      this.registerHandler(
        layer,
        "mouseover",
        onMouseOver,
        `layer_${layerId}_mouseover`,
      );
    }

    if (onMouseOut) {
      this.registerHandler(
        layer,
        "mouseout",
        onMouseOut,
        `layer_${layerId}_mouseout`,
      );
    }
  }

  /**
   * Removes all event handlers for a specific layer
   * @param {string} layerId - Layer identifier
   */
  cleanupLayerEvents(layerId) {
    const keysToRemove = [];
    for (const key of this.eventHandlers.keys()) {
      if (key.startsWith(`layer_${layerId}_`)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => this.removeHandler(key));
  }

  /**
   * Creates a batch update handler that collects multiple changes
   * @param {Function} onBatchUpdate - Batch update callback
   * @param {number} batchDelay - Delay to wait for more changes
   * @returns {Function} Batch update handler
   */
  createBatchUpdateHandler(onBatchUpdate, batchDelay = 500) {
    let changesBatch = [];

    return this.debounce(
      () => {
        if (changesBatch.length > 0) {
          onBatchUpdate([...changesBatch]);
          changesBatch = [];
        }
      },
      batchDelay,
      "batchUpdate",
    );
  }

  /**
   * Adds a change to the batch
   * @param {Object} change - Change object
   */
  addToBatch(change) {
    // This would be used with createBatchUpdateHandler
    // Implementation depends on specific use case
  }
}

// Create a singleton instance
const mapEventService = new MapEventService();

export default mapEventService;

